import {gaEvent} from 'common/analytics';
import {trackSignup} from 'iq-onboarding/tracking';
import {track} from 'iq-tracking';
import type {SignupCtaSurface} from 'iq-auth/signup_cta';

// Account step + recovery banner of the unified `signup` event. Reuses the
// onboarding `trackSignup` helper so the event/content_type stay consistent.
export type SignupMethod = 'oauth-google' | 'oauth-linkedin' | 'password';

// GA4 `sign_up` uses a clean `method` value (google | linkedin | email).
const GA_SIGNUP_METHOD: Record<SignupMethod, string> = {
  'oauth-google': 'google',
  'oauth-linkedin': 'linkedin',
  password: 'email',
};

export const trackSignupMethodSelected = (method: SignupMethod): Promise<void> =>
  trackSignup({
    step: 'account',
    trigger: 'method_selected',
    action: 'click',
    extra: {method},
    // OAuth buttons navigate to the provider immediately — let the POST survive.
    keepalive: method !== 'password',
  });

export const trackSignupAccountSubmitted = (): Promise<void> =>
  trackSignup({
    step: 'account',
    trigger: 'account_submitted',
    action: 'submit',
    extra: {method: 'password'},
  });

export const trackSignupCompleted = (method: SignupMethod): Promise<void> => {
  // GA4 recommended event for account creation (prod-only inside gaEvent).
  gaEvent('sign_up', {method: GA_SIGNUP_METHOD[method]});

  return trackSignup({
    step: 'account',
    trigger: 'signup_completed',
    action: 'submit',
    extra: {method},
    // Fired right before the post-signup redirect.
    keepalive: true,
  });
};

export const trackSignupFailed = (method: SignupMethod, reason: string): Promise<void> =>
  trackSignup({
    step: 'account',
    trigger: 'signup_failed',
    action: 'submit',
    extra: {method, reason},
  });

export const trackAuthErrorShown = (
  code: 'signup-user-exists' | 'login-user-not-found',
): Promise<void> =>
  trackSignup({step: 'recovery', trigger: 'auth_error_shown', action: 'view', extra: {code}});

/**
 * Post-signup onboarding (shipped from post_signup_onboarding_v1): a gate's signup button sent the visitor to the
 * full /signup flow (ending on pricing) instead of opening the inline form.
 */
export const trackPostSignupOnboardingRedirected = (surface: SignupCtaSurface): Promise<void> =>
  track({
    event: 'post_signup_onboarding_redirected',
    action: 'click',
    component: 'barrier',
    content_type: surface,
    extra: {extra_data: JSON.stringify({variant: 'experiment', surface})},
    // Fired right before navigating to /signup.
    keepalive: true,
  });
